import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { backendServices } from '../../services';

interface SplitScreenDocumentViewContextType {
    splitScreenDocumentView: boolean;
    splitScreenDocumentViewSupported: boolean;
    setSplitScreenDocumentView: (splitScreenDocumentView: boolean) => void;
    clearSplitScreenDocumentView: () => void;
    generatedPDF: backendServices.ViewModels.GeneratePDFUIViewModel | null;
    setPDFData: (pdfData: backendServices.ViewModels.GeneratePDFUIViewModel | null) => void;
}

const defaultSplitScreenDocumentViewContext: SplitScreenDocumentViewContextType = {
    splitScreenDocumentView: false,
    splitScreenDocumentViewSupported: false,
    setSplitScreenDocumentView: () => {},
    clearSplitScreenDocumentView: () => {},
    generatedPDF: null,
    setPDFData: () => {},
};

const SplitScreenDocumentViewContext = createContext<SplitScreenDocumentViewContextType>(
    defaultSplitScreenDocumentViewContext
);

interface SplitScreenDocumentViewProviderProps {
    children: ReactNode;
    splitScreenDocumentViewSupported?: boolean;
}

const SPLIT_SCREEN_LOCAL_STORAGE_KEY = 'splitScreenDocumentView';

export const SplitScreenDocumentViewProvider: React.FC<SplitScreenDocumentViewProviderProps> = ({
    children,
    splitScreenDocumentViewSupported = true, // we can force this to false if we want to disable the split screen document view. For example, non invoice web entry mode
}) => {
    const storedSplitScreenDocumentViewSetting = localStorage.getItem(SPLIT_SCREEN_LOCAL_STORAGE_KEY);

    const [splitScreenDocumentView, setSplitScreenDocumentView] = useState<boolean>(
        storedSplitScreenDocumentViewSetting === 'true' && splitScreenDocumentViewSupported ? true : false
    );

    const [generatedPDF, setGeneratedPDF] = useState<backendServices.ViewModels.GeneratePDFUIViewModel | null>(null);

    useEffect(() => {
        localStorage.setItem(SPLIT_SCREEN_LOCAL_STORAGE_KEY, splitScreenDocumentView ? 'true' : 'false');
    }, [splitScreenDocumentView]);

    const clearSplitScreenDocumentView = useCallback(() => {
        localStorage.removeItem(SPLIT_SCREEN_LOCAL_STORAGE_KEY);
        setSplitScreenDocumentView(false);
    }, []);

    const setPDFData = useCallback(
        (pdfData: backendServices.ViewModels.GeneratePDFUIViewModel | null) => {
            const hasChanged =
                (pdfData == null && generatedPDF != null) ||
                (pdfData != null && generatedPDF == null) ||
                pdfData?.fileName !== generatedPDF?.fileName ||
                pdfData?.dataBlob?.size !== generatedPDF?.dataBlob?.size;

            if (hasChanged) {
                setGeneratedPDF(pdfData);
            }
        },
        [generatedPDF]
    );

    const contextValue = useMemo(
        () => ({
            splitScreenDocumentView,
            splitScreenDocumentViewSupported: splitScreenDocumentViewSupported,
            setSplitScreenDocumentView,
            clearSplitScreenDocumentView,
            generatedPDF,
            setPDFData,
        }),
        [
            splitScreenDocumentView,
            clearSplitScreenDocumentView,
            generatedPDF,
            setPDFData,
            splitScreenDocumentViewSupported,
        ]
    );

    return (
        <SplitScreenDocumentViewContext.Provider value={contextValue}>
            {children}
        </SplitScreenDocumentViewContext.Provider>
    );
};

export const useSplitScreenDocumentView = () => {
    return useContext(SplitScreenDocumentViewContext);
};

export function withSplitScreenDocumentView<P>(
    Component: React.ComponentType<P & { splitScreenDocumentViewContext: SplitScreenDocumentViewContextType }>
): React.ComponentType<P> {
    return (props: P) => {
        const splitScreenDocumentViewContext = useSplitScreenDocumentView();

        return (
            <Component
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...props}
                splitScreenDocumentViewContext={splitScreenDocumentViewContext}
            />
        );
    };
}
